import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { mapCatalog, toCatalogRequest } from './actionMappers';
import type { ClarifyCatalogAnswer, ClarifyCatalogInput } from './actionTypes';
import { type ProviderConfigError, parsedErrorOrNull } from './errors';
import { useForgetRequest } from './forgetRequest';
import { listClarifyCatalogRequest } from './secretRequests';

export interface UseClarifyCatalogResult {
    /** Rejects with the raw error; `error` holds it parsed. */
    load: (input: ClarifyCatalogInput) => Promise<ClarifyCatalogAnswer>;
    isLoading: boolean;
    error: ProviderConfigError | null;
    /** Drops the request, typed secrets included, from the mutation cache. */
    reset: () => void;
}

/**
 * A POST rather than a query: the listing runs with the unsaved connection values, and
 * a cache keyed on them would hold typed secrets.
 */
export function useClarifyCatalog(locationId: number): UseClarifyCatalogResult {
    const { mutationKey, forget } = useForgetRequest('catalog');
    const mutation = useMutation({
        mutationKey,
        mutationFn: listClarifyCatalogRequest,
        // The variables, and error.response.config.data on a failure, carry typed secrets.
        // Without this the mutation keeps them in the cache for 5 minutes after unmount;
        // while mounted they live until reset.
        gcTime: 0,
    });
    const { mutateAsync } = mutation;

    const load = useCallback(
        async (input: ClarifyCatalogInput) => {
            const response = await mutateAsync({
                locationId,
                requestBody: toCatalogRequest(input),
            });
            return mapCatalog(response.data);
        },
        [mutateAsync, locationId]
    );
    const error = useMemo(() => parsedErrorOrNull(mutation.error), [mutation.error]);
    const { reset: resetResult } = mutation;
    const reset = useCallback(() => {
        resetResult();
        forget();
    }, [resetResult, forget]);

    return { load, isLoading: mutation.isPending, error, reset };
}
